/**
 * The DOM overlay renders the interface (resource bars, buy lists, upgrade grid,
 * breakthrough panel, tabs, toasts, modal) in front of the Pixi canvas — plain
 * TypeScript, no UI framework (tech-design §4). It never mutates game state: it
 * renders a selector-derived {@link OverlayViewModel} and dispatches typed
 * {@link OverlayIntent}s back to the app, which turns them into engine commands.
 *
 * Updates flow through the hand-rolled reactive layer: `render(vm)` sets a
 * signal, and an `effect` reconciles the DOM in place (keyed by id — no
 * per-tick teardown, so buttons keep their handlers and focus).
 */
import { tagComponent } from './debugLabel.js';
import { button, el } from './dom.js';
import { amountView, artIcon } from './number.js';
import { type Signal, effect, signal } from './reactive.js';

/** Glorp's bust portrait for the narration bubble (art/character/glorp-zeblak/,
 * mirrored into the build at the same path — see art/README.md). */
const GLORP_HEAD_URL = '/art/character/glorp-zeblak/head.png';

/** One generator's contribution to a resource — shown in the resource's detail
 * popup, ordered most to least by contribution size. */
export interface ResourceContributorView {
  readonly label: string;
  readonly artUrl?: string | undefined;
  /** This generator's net contribution per tick, formatted with sign. */
  readonly perTickDisplay: string;
  /** Its share of the resource's total contributor activity (`"42%"`, no
   * decimals — the list is a breakdown, not a precise measurement). */
  readonly sharePctDisplay: string;
}

/** A resource line: current amount plus its production rate. */
export interface ResourceView {
  readonly id: string;
  readonly label: string;
  readonly description: string;
  /** Human-formatted amount (`1.23 M`). */
  readonly display: string;
  /** Units per second, already formatted **with an explicit sign** (`+1.23 K`). */
  readonly rateDisplay: string;
  /** The same net rate per tick (not per second) — matches the unit each
   * contributor row is shown in, for the detail popup's summary line. */
  readonly totalPerTickDisplay: string;
  /** `true` for the primary resource shown large at the top. */
  readonly primary: boolean;
  /** Generated-art icon URL; the row hides the icon if it fails to load. */
  readonly artUrl?: string | undefined;
  /** Every owned generator/converter touching this resource, for the detail popup. */
  readonly contributors: readonly ResourceContributorView[];
}

/** What buying a generator at a given quantity mode would cost right now. */
export interface GeneratorBuyOption {
  /** Units this option actually buys (for `max`, however many are affordable). */
  readonly count: number;
  /** Just the formatted amount (`33.5 K`) — the cost resource's icon carries
   * which resource, so its name doesn't have to (it would crowd out the
   * generator's own name in the row). */
  readonly costDisplay: string;
  readonly affordable: boolean;
}

/** One resource a generator produces or consumes — shown in its info popover. */
export interface GeneratorYieldView {
  readonly resourceLabel: string;
  readonly resourceArtUrl?: string | undefined;
  /** Per-owned-unit rate, already formatted with sign (`+1.23 K/s`). */
  readonly ratePerSecDisplay: string;
}

/** A generator row, priced for each of the three buy-quantity modes. */
export interface GeneratorView {
  readonly id: string;
  readonly label: string;
  readonly description: string;
  readonly owned: number;
  /** Icon for the resource all three buy options are priced in. */
  readonly costResourceArtUrl?: string | undefined;
  /** The cost resource's name — screen-reader only; the row shows its icon. */
  readonly costResourceLabel: string;
  readonly buy1: GeneratorBuyOption;
  readonly buy10: GeneratorBuyOption;
  readonly buyMax: GeneratorBuyOption;
  /** Empty if the generator is a pure sink with nothing to show as output. */
  readonly produces: readonly GeneratorYieldView[];
  readonly consumes: readonly GeneratorYieldView[];
  /** Generated-art icon URL; the row hides the icon if it fails to load. */
  readonly artUrl?: string | undefined;
}

/** An upgrade: one-shot purchase, then owned. Same entity-row shape as
 * generators and breakthroughs. */
export interface UpgradeView {
  readonly id: string;
  readonly label: string;
  readonly description: string;
  /** Just the formatted amount — the cost resource's icon carries which
   * resource, same convention as a generator's cost. */
  readonly costDisplay: string;
  readonly costResourceArtUrl?: string | undefined;
  /** Screen-reader only; the row shows the resource's icon instead. */
  readonly costResourceLabel: string;
  readonly owned: boolean;
  readonly canAfford: boolean;
  /** Generated-art icon URL; the row hides the icon if it fails to load. */
  readonly artUrl?: string | undefined;
}

/** One resource an attempt costs — shown as an icon, not spelled-out name. */
export interface BreakthroughCostView {
  readonly resourceArtUrl?: string | undefined;
  /** Screen-reader only; the row shows the resource's icon instead. */
  readonly resourceLabel: string;
  readonly amountDisplay: string;
}

/** A breakthrough with a chance and a pity progress bar. */
export interface BreakthroughView {
  readonly id: string;
  readonly label: string;
  readonly description: string;
  readonly chancePct: number;
  readonly pity: number;
  readonly pityMax: number;
  readonly canAttempt: boolean;
  /** Always at least one (content schema requires a non-empty cost). */
  readonly costs: readonly BreakthroughCostView[];
  /** Generated-art icon URL; the row hides the icon if it fails to load. */
  readonly artUrl?: string | undefined;
}

export interface OverlayViewModel {
  readonly ticks: number;
  readonly resources: readonly ResourceView[];
  readonly generators: readonly GeneratorView[];
  readonly upgrades: readonly UpgradeView[];
  readonly breakthroughs: readonly BreakthroughView[];
}

/** What the player wants to do. The app maps these to engine commands. */
export type OverlayIntent =
  | { readonly type: 'click' }
  | {
      readonly type: 'buyGenerator';
      readonly id: string;
      readonly count: 1 | 10 | 'max';
    }
  | { readonly type: 'buyUpgrade'; readonly id: string }
  | { readonly type: 'attemptBreakthrough'; readonly id: string }
  | { readonly type: 'openSettings' };

export interface Overlay {
  /** Push the latest view model; the reactive layer reconciles the DOM. */
  render(vm: OverlayViewModel): void;
  /** Show a transient toast (unlock notice, breakthrough hit). */
  toast(message: string): void;
  /** Show a blocking modal (e.g. "while you were away"). */
  showModal(title: string, body: string): void;
  /** Show Glorp's line for a just-bought/activated entity. Replaces whatever
   * narration is currently showing; dismissed by a tap or the next call. */
  narrate(line: string): void;
}

type TabId = 'world' | 'upgrades' | 'breakthroughs';

const EMPTY_VM: OverlayViewModel = {
  ticks: 0,
  resources: [],
  generators: [],
  upgrades: [],
  breakthroughs: [],
};

/** A keyed row that builds itself once and updates in place from its holder. */
interface KeyedRow {
  readonly root: HTMLElement;
  update(): void;
}

interface CacheEntry<T> {
  readonly row: KeyedRow;
  readonly holder: { item: T };
}

/**
 * Reconcile a keyed list into `host`: create rows for new ids, update existing
 * ones in place, remove departed ones, and keep document order aligned with
 * `items`.
 */
function reconcile<T extends { readonly id: string }>(
  host: HTMLElement,
  cache: Map<string, CacheEntry<T>>,
  items: readonly T[],
  create: (getItem: () => T) => KeyedRow,
): void {
  const seen = new Set<string>();
  items.forEach((item, index) => {
    seen.add(item.id);
    let entry = cache.get(item.id);
    if (!entry) {
      const holder = { item };
      entry = { row: create(() => holder.item), holder };
      cache.set(item.id, entry);
    }
    entry.holder.item = item;
    entry.row.update();
    const at = host.children[index];
    if (at !== entry.row.root) {
      host.insertBefore(entry.row.root, at ?? null);
    }
  });
  for (const [id, entry] of cache) {
    if (!seen.has(id)) {
      entry.row.root.remove();
      cache.delete(id);
    }
  }
}

export function createOverlay(
  mount: HTMLElement,
  dispatch: (intent: OverlayIntent) => void,
  topbarMount: HTMLElement,
): Overlay {
  const vm: Signal<OverlayViewModel> = signal<OverlayViewModel>(EMPTY_VM);
  const activeTab: Signal<TabId> = signal<TabId>('world');
  /** How many units a generator tap buys; a global toggle above the list. */
  const buyMode: Signal<1 | 10 | 'max'> = signal<1 | 10 | 'max'>(1);

  // ---- full-width top bar: every resource as icon · name · amount ----------
  // A persistent HUD strip across the top of the viewport (desktop and phone).
  // Tickrate peeks on hover (desktop); a click/tap opens the resource's full
  // detail popup. Its measured height feeds `--topbar-h`, which offsets the
  // docked panel below it.
  const topbar = topbarMount;
  topbar.dataset.testid = 'topbar';
  tagComponent(topbar, 'topbar');
  const setTopbarHeight = (): void => {
    document.documentElement.style.setProperty(
      '--topbar-h',
      `${String(topbar.offsetHeight)}px`,
    );
  };

  /** `"1.23 K"` → `"1 K"` — drop the fractional part, keep the unit/suffix. */
  const stripDecimals = (text: string): string => text.replace(/\.\d+/, '');

  const setTopbarAmountsTerse = (terse: boolean): void => {
    for (const node of topbar.querySelectorAll<HTMLElement>(
      '.topbar-amount, .topbar-rate',
    )) {
      const full = node.dataset.full ?? '';
      node.textContent = terse ? stripDecimals(full) : full;
    }
  };

  /**
   * Never let the top bar spill to a second row: if it doesn't fit at full
   * width, hide resource names first, then drop amounts to whole numbers. Runs
   * from scratch each time so it also un-degrades once space frees up (a
   * resource going away, or a wider viewport) — unlike the tab menu items,
   * this is a layout fit, not a progressive unlock.
   */
  const fitTopbar = (): void => {
    topbar.classList.remove('topbar-compact', 'topbar-terse');
    setTopbarAmountsTerse(false);
    if (topbar.scrollWidth <= topbar.clientWidth) return;
    topbar.classList.add('topbar-compact');
    if (topbar.scrollWidth <= topbar.clientWidth) return;
    topbar.classList.add('topbar-terse');
    setTopbarAmountsTerse(true);
  };

  new ResizeObserver(() => {
    setTopbarHeight();
    fitTopbar();
  }).observe(topbar);

  // No panel header left: the tick clock is only exposed via `panel.dataset.ticks`
  // (observability for tests), and settings moved into the tab bar as a menu
  // item next to Breakthroughs — see below.

  // The click/absorb action is now a tap anywhere on the canvas (app/main.ts)
  // rather than a dedicated button — it was taking up panel space that the
  // generator list needs more.

  // ---- tab panels ----------------------------------------------------------
  const worldPanel = el('section', { class: 'panel', testid: 'panel-world' });
  const upgradesPanel = el('section', { class: 'panel', testid: 'panel-upgrades' });
  const breakthroughPanel = el('section', {
    class: 'panel',
    testid: 'panel-breakthroughs',
  });
  // Not tagComponent-ed: a full-height section's bottom-left corner coincides
  // with whatever's last inside it, so its label would just overlap that
  // component's own — not useful, unlike a self-contained widget's label.

  // Resource amounts/rates already live in the full-width top bar — a "Colony"
  // heading here would just repeat them under a second label.
  const buyModes: readonly { id: 1 | 10 | 'max'; label: string }[] = [
    { id: 1, label: '×1' },
    { id: 10, label: '×10' },
    { id: 'max', label: 'Max' },
  ];
  const buyModeButtons = new Map<1 | 10 | 'max', HTMLButtonElement>();
  const buyModeBar = el('div', { class: 'buy-mode-bar', testid: 'buy-mode-bar' });
  for (const mode of buyModes) {
    const btn = button(
      mode.label,
      () => {
        buyMode.value = mode.id;
      },
      { class: 'buy-mode', testid: `buy-mode-${String(mode.id)}` },
    );
    buyModeButtons.set(mode.id, btn);
    buyModeBar.append(btn);
  }
  // No panel heading here: the tab bar already names the active section
  // ("World"/"Upgrades"/"Breakthroughs") immediately above — repeating it as an
  // h2 just burned a line of the panel's limited height on every tab.
  const generatorList = el('div', { class: 'generator-list' });
  worldPanel.append(buyModeBar, generatorList);
  const upgradeList = el('div', { class: 'upgrade-list' });
  upgradesPanel.append(upgradeList);
  const breakthroughList = el('div', { class: 'breakthrough-list' });
  breakthroughPanel.append(breakthroughList);

  const tabs: readonly {
    id: TabId;
    label: string;
    panel: HTMLElement;
    /** How many items would show on this tab right now. */
    count: (m: OverlayViewModel) => number;
  }[] = [
    { id: 'world', label: 'World', panel: worldPanel, count: (m) => m.generators.length },
    {
      id: 'upgrades',
      label: 'Upgrades',
      panel: upgradesPanel,
      count: (m) => m.upgrades.length,
    },
    {
      id: 'breakthroughs',
      label: 'Breakthroughs',
      panel: breakthroughPanel,
      count: (m) => m.breakthroughs.length,
    },
  ];
  const tabButtons = new Map<TabId, HTMLButtonElement>();
  // A tab is a menu item only once it has something to show — sticky once
  // reached, so it never disappears again (e.g. across an age transition
  // where the new age's breakthroughs haven't unlocked yet).
  const shownTabs = new Set<TabId>();
  const tabBar = el('nav', { class: 'tab-bar' });
  tagComponent(tabBar, 'tab-bar');
  for (const tab of tabs) {
    const btn = button(
      tab.label,
      () => {
        activeTab.value = tab.id;
      },
      { class: 'tab hidden', testid: `tab-${tab.id}` },
    );
    tabButtons.set(tab.id, btn);
    tabBar.append(btn);
  }
  // Settings lives as a menu item next to Breakthroughs — it opens a modal
  // rather than switching panels, so it sits outside the `tabs`/`activeTab` wiring.
  const settingsButton = button(
    '⚙',
    () => {
      dispatch({ type: 'openSettings' });
    },
    { class: 'tab settings-tab', testid: 'open-settings', title: 'Settings' },
  );
  settingsButton.setAttribute('aria-label', 'Settings');
  tabBar.append(settingsButton);

  // ---- toasts + modal ------------------------------------------------------
  const toastHost = el('div', { class: 'toast-host', testid: 'toast-host' });
  const modalHost = el('div', { class: 'modal-host hidden', testid: 'modal-host' });

  /**
   * A large portrait for the info popup — unlike a row's small `artIcon`, a
   * missing/failed image collapses its whole column (not just the `<img>`),
   * so the popup falls back to a single column of text instead of leaving an
   * empty gap where the art would be.
   */
  function infoModalImage(): {
    readonly root: HTMLElement;
    set(url: string | undefined): void;
  } {
    const img = el('img', { class: 'info-modal-image' });
    img.alt = '';
    img.hidden = true;
    const wrap = el('div', { class: 'info-modal-image-wrap hidden' }, [img]);
    img.addEventListener('load', () => {
      img.hidden = false;
      wrap.classList.remove('hidden');
    });
    img.addEventListener('error', () => {
      img.hidden = true;
      wrap.classList.add('hidden');
    });
    return {
      root: wrap,
      set(url: string | undefined): void {
        if (url === undefined) {
          img.hidden = true;
          wrap.classList.add('hidden');
          return;
        }
        if (img.dataset.src !== url) {
          img.dataset.src = url;
          img.src = url;
        }
      },
    };
  }

  /**
   * The "more info" popup: clicking a resource/generator/breakthrough/upgrade's
   * icon and name shows what it is, in place of the row's cramped one-liner.
   * Backed by the same `.modal-host` as `showModal` (below) — two columns, a
   * large portrait (`artUrl`, when the entity has one) beside the text: `rows`
   * renders an icon/label/value list under the description (a resource's
   * per-generator breakdown, a generator's outputs).
   */
  function openInfoModal(
    title: string,
    description: string,
    rows: readonly { artUrl?: string | undefined; label: string; value: string }[] = [],
    kind = 'info-modal',
    artUrl?: string,
  ): void {
    const bodyChildren: HTMLElement[] = [el('h2', { text: title })];
    if (description !== '') {
      bodyChildren.push(el('p', { class: 'info-desc', text: description }));
    }
    if (rows.length > 0) {
      bodyChildren.push(
        el(
          'ul',
          { class: 'info-rows' },
          rows.map((row) => {
            const icon = artIcon('info-row-icon');
            icon.set(row.artUrl);
            return el('li', { class: 'info-row' }, [
              icon.img,
              el('span', { class: 'info-row-label', text: row.label }),
              el('span', { class: 'info-row-value', text: row.value }),
            ]);
          }),
        ),
      );
    }
    bodyChildren.push(
      button(
        'Continue',
        () => {
          modalHost.classList.add('hidden');
        },
        { class: 'buy', testid: 'modal-dismiss' },
      ),
    );
    const image = infoModalImage();
    image.set(artUrl);
    const body = el('div', { class: 'info-modal-body' }, bodyChildren);
    const dialog = el('div', { class: 'modal info-modal' }, [image.root, body]);
    tagComponent(dialog, kind);
    modalHost.replaceChildren(dialog);
    modalHost.classList.remove('hidden');
  }

  // ---- narration (Glorp) ----------------------------------------------------
  // Bottom-left on desktop, over the bottom of the canvas on phone (CSS handles
  // the split — see .narration-host). One line at a time: a tap or the next
  // `narrate()` call dismisses whatever is showing.
  const narrationPortrait = el('img', { class: 'narration-portrait' });
  narrationPortrait.alt = 'Glorp';
  narrationPortrait.src = GLORP_HEAD_URL;
  const narrationText = el('p', { class: 'narration-text' });
  const narrationBubble = el('div', { class: 'narration-bubble' }, [narrationText]);
  const narrationHost = el(
    'div',
    { class: 'narration-host hidden', testid: 'narration' },
    [narrationPortrait, narrationBubble],
  );
  narrationHost.addEventListener('click', () => {
    narrationHost.classList.add('hidden');
  });
  tagComponent(narrationHost, 'narration-host');

  const panel = el('div', { class: 'overlay-panel', testid: 'overlay-panel' }, [
    tabBar,
    worldPanel,
    upgradesPanel,
    breakthroughPanel,
  ]);
  // Not tagComponent-ed: full viewport height, same overlap reasoning as the
  // panels above.
  mount.append(panel, toastHost, modalHost, narrationHost);

  // ---- keyed caches --------------------------------------------------------
  const topbarCache = new Map<string, CacheEntry<ResourceView>>();
  const generatorCache = new Map<string, CacheEntry<GeneratorView>>();
  const upgradeCache = new Map<string, CacheEntry<UpgradeView>>();
  const breakthroughCache = new Map<string, CacheEntry<BreakthroughView>>();

  const makeTopbarResource = (get: () => ResourceView): KeyedRow => {
    const icon = artIcon('topbar-icon');
    const name = el('span', { class: 'topbar-name' });
    const amount = el('span', { class: 'topbar-amount' });
    // The rate still peeks on hover (desktop, CSS-only). A click/tap no longer
    // toggles that peek — it opens the full detail popup instead (name, exact
    // amount, description, and every generator's per-tick contribution).
    const rate = el('span', { class: 'topbar-rate' });
    const root = el('div', { class: 'topbar-res' }, [icon.img, name, amount, rate]);
    tagComponent(root, 'topbar-res', get().id);
    root.addEventListener('click', () => {
      const r = get();
      openInfoModal(
        r.label,
        [r.description, `Total: ${r.totalPerTickDisplay}/tick`]
          .filter((line) => line !== '')
          .join('\n\n'),
        r.contributors.map((c) => ({
          artUrl: c.artUrl,
          label: c.label,
          value: `${c.perTickDisplay}/tick (${c.sharePctDisplay})`,
        })),
        `info-modal (resource)#${r.id}`,
        r.artUrl,
      );
    });
    return {
      root,
      update(): void {
        const r = get();
        root.dataset.testid = `topbar-resource-${r.id}`;
        icon.set(r.artUrl);
        name.textContent = r.label;
        // `fitTopbar` re-derives the displayed (possibly terse) text from
        // these `full` stashes rather than reformatting the number itself.
        amount.dataset.full = r.display;
        amount.textContent = r.display;
        rate.dataset.full = `${r.rateDisplay}/s`;
        rate.textContent = `${r.rateDisplay}/s`;
        // The primary resource carries the canonical amount/rate test hooks.
        if (r.primary) {
          amount.dataset.testid = 'resource-primary';
          rate.dataset.testid = 'rate-readout';
        }
        root.setAttribute('aria-label', `${r.label}: ${r.display} (${r.rateDisplay}/s)`);
      },
    };
  };

  /**
   * A prominent entity icon (generator or breakthrough — the two share one row
   * component) with a pulsing skeleton shown until the generated art loads (or
   * forever, on error — there's nothing to show).
   */
  function entityIcon(): {
    readonly root: HTMLElement;
    set(url: string | undefined): void;
  } {
    const img = el('img', { class: 'entity-icon' });
    img.alt = '';
    img.hidden = true;
    const skeleton = el('div', { class: 'entity-icon-skeleton' });
    const root = el('div', { class: 'entity-icon-wrap' }, [skeleton, img]);
    img.addEventListener('load', () => {
      img.hidden = false;
      skeleton.hidden = true;
    });
    img.addEventListener('error', () => {
      img.hidden = true;
      skeleton.hidden = true;
    });
    return {
      root,
      set(url: string | undefined): void {
        if (url === undefined) {
          img.hidden = true;
          skeleton.hidden = true;
          return;
        }
        if (img.dataset.src !== url) {
          img.dataset.src = url;
          img.hidden = true;
          skeleton.hidden = false;
          img.src = url;
        }
      },
    };
  }

  const buyOptionFor = (g: GeneratorView, mode: 1 | 10 | 'max'): GeneratorBuyOption => {
    switch (mode) {
      case 1:
        return g.buy1;
      case 10:
        return g.buy10;
      case 'max':
        return g.buyMax;
    }
  };

  /** Generators and breakthroughs share one row shape (§CLAUDE.md: same UI
   * component) — an info button (icon + name, opens the description popup) and
   * a fixed-size, distinctly-styled buy/attempt button that turns green the
   * moment it's affordable. */
  const makeGeneratorRow = (get: () => GeneratorView): KeyedRow => {
    const icon = entityIcon();
    const name = el('span', { class: 'entity-name' });
    const meta = el('span', { class: 'entity-meta' });
    const main = el('button', { class: 'entity-main' }, [
      icon.root,
      el('span', { class: 'entity-text' }, [name, meta]),
    ]);
    main.type = 'button';
    main.addEventListener('click', () => {
      const g = get();
      openInfoModal(
        g.label,
        g.description,
        [...g.produces, ...g.consumes].map((y) => ({
          artUrl: y.resourceArtUrl,
          label: y.resourceLabel,
          value: `${y.ratePerSecDisplay}/s each`,
        })),
        `info-modal (generator)#${g.id}`,
        g.artUrl,
      );
    });

    const cost = amountView('entity-cost', 'entity-cost-icon', 'entity-cost-amount');
    // Only meaningful in ×Max mode (1 and 10 always buy exactly that many) —
    // how many the current stock actually affords, right-aligned under the price.
    const maxCount = el('span', { class: 'entity-buy-count' });
    const buy = el('button', { class: 'entity-buy' }, [cost.root, maxCount]);
    buy.type = 'button';
    buy.addEventListener('click', () => {
      dispatch({ type: 'buyGenerator', id: get().id, count: buyMode.peek() });
    });

    const root = el('div', { class: 'entity-row' }, [main, buy]);
    tagComponent(root, 'entity-row (generator)', get().id);
    return {
      root,
      update(): void {
        const g = get();
        const mode = buyMode.value;
        const option = buyOptionFor(g, mode);
        root.dataset.testid = `generator-row-${g.id}`;
        main.dataset.testid = `generator-info-${g.id}`;
        buy.dataset.testid = `generator-${g.id}`;
        icon.set(g.artUrl);
        name.textContent = g.label;
        meta.textContent = `×${String(g.owned)}`;
        cost.set(option.costDisplay, g.costResourceArtUrl);
        maxCount.textContent = mode === 'max' ? `×${String(g.buyMax.count)}` : '';
        root.classList.toggle('unaffordable', !option.affordable);
        buy.classList.toggle('affordable', option.affordable);
        buy.disabled = !option.affordable;
        main.setAttribute('aria-label', `${g.label}: more info`);
        buy.setAttribute(
          'aria-label',
          `Buy ${g.label}: own ${String(g.owned)}, cost ${option.costDisplay} ${
            g.costResourceLabel
          }${option.affordable ? '' : ', not enough resources'}`,
        );
      },
    };
  };

  // Same entity-row shape as generators/breakthroughs (icon + name info button,
  // fixed-size buy button) — upgrades have generated art too, and a one-off
  // purchase reads the same as any other buy action once it's owned.
  const makeUpgradeCard = (get: () => UpgradeView): KeyedRow => {
    const icon = entityIcon();
    const name = el('span', { class: 'entity-name' });
    const meta = el('span', { class: 'entity-meta' });
    const main = el('button', { class: 'entity-main' }, [
      icon.root,
      el('span', { class: 'entity-text' }, [name, meta]),
    ]);
    main.type = 'button';
    main.addEventListener('click', () => {
      const u = get();
      openInfoModal(u.label, u.description, [], `info-modal (upgrade)#${u.id}`, u.artUrl);
    });

    const cost = amountView('entity-cost', 'entity-cost-icon', 'entity-cost-amount');
    const buy = el('button', { class: 'entity-buy' }, [cost.root]);
    buy.type = 'button';
    buy.addEventListener('click', () => {
      dispatch({ type: 'buyUpgrade', id: get().id });
    });

    const root = el('div', { class: 'entity-row' }, [main, buy]);
    tagComponent(root, 'entity-row (upgrade)', get().id);
    return {
      root,
      update(): void {
        const u = get();
        root.dataset.testid = `upgrade-row-${u.id}`;
        main.dataset.testid = `upgrade-info-${u.id}`;
        buy.dataset.testid = `buy-upgrade-${u.id}`;
        icon.set(u.artUrl);
        name.textContent = u.label;
        meta.textContent = u.owned ? 'Owned' : '';
        root.classList.toggle('unaffordable', !u.owned && !u.canAfford);
        buy.classList.toggle('affordable', !u.owned && u.canAfford);
        buy.disabled = u.owned || !u.canAfford;
        cost.set(
          u.owned ? 'Owned' : u.costDisplay,
          u.owned ? undefined : u.costResourceArtUrl,
        );
        main.setAttribute('aria-label', `${u.label}: more info`);
        buy.setAttribute(
          'aria-label',
          u.owned
            ? `${u.label}: already owned`
            : `Buy ${u.label}: cost ${u.costDisplay} ${u.costResourceLabel}`,
        );
      },
    };
  };

  const makeBreakthroughRow = (get: () => BreakthroughView): KeyedRow => {
    const icon = entityIcon();
    const name = el('span', { class: 'entity-name' });
    const meta = el('span', { class: 'entity-meta' });
    const main = el('button', { class: 'entity-main' }, [
      icon.root,
      el('span', { class: 'entity-text' }, [name, meta]),
    ]);
    main.type = 'button';
    main.addEventListener('click', () => {
      const b = get();
      openInfoModal(
        b.label,
        b.description,
        [],
        `info-modal (breakthrough)#${b.id}`,
        b.artUrl,
      );
    });

    // Cost resources are fixed for a breakthrough's lifetime (content-defined),
    // so the icon/amount pairs are built once here; update() only refreshes
    // the amount text and the icon URL (in case art finishes loading late).
    const initialCosts = get().costs;
    const costParts = initialCosts.map(() =>
      amountView('entity-cost', 'entity-cost-icon', 'entity-cost-amount'),
    );
    const attempt = el(
      'button',
      { class: 'entity-buy' },
      costParts.map((c) => c.root),
    );
    attempt.type = 'button';
    attempt.addEventListener('click', () => {
      dispatch({ type: 'attemptBreakthrough', id: get().id });
    });

    const row = el('div', { class: 'entity-row' }, [main, attempt]);
    // The pity bar moves under the whole row, full-width and unlabeled (no
    // percentage, no "Pity" text) — a fixed-size attempt button no longer has
    // room to carry it beside the cost.
    const pityFill = el('div', { class: 'entity-progress-fill' });
    const pityBar = el('div', { class: 'entity-progress' }, [pityFill]);
    pityBar.setAttribute('aria-label', 'Pity progress — guarantees a hit');
    const root = el('div', { class: 'entity-row-wrap' }, [row, pityBar]);
    tagComponent(root, 'entity-row (breakthrough)', get().id);
    return {
      root,
      update(): void {
        const b = get();
        root.dataset.testid = `breakthrough-${b.id}`;
        main.dataset.testid = `breakthrough-info-${b.id}`;
        icon.set(b.artUrl);
        name.textContent = b.label;
        meta.textContent = `${b.chancePct.toFixed(1)}%`;
        const pct = b.pityMax > 0 ? Math.min(100, (b.pity / b.pityMax) * 100) : 0;
        pityFill.style.width = `${String(pct)}%`;
        pityBar.dataset.testid = `pity-${b.id}`;
        b.costs.forEach((c, i) => {
          costParts[i]?.set(c.amountDisplay, c.resourceArtUrl);
        });
        row.classList.toggle('unaffordable', !b.canAttempt);
        attempt.classList.toggle('affordable', b.canAttempt);
        attempt.disabled = !b.canAttempt;
        attempt.dataset.testid = `attempt-${b.id}`;
        main.setAttribute('aria-label', `${b.label}: more info`);
        attempt.setAttribute(
          'aria-label',
          `Attempt: ${b.costs.map((c) => `${c.amountDisplay} ${c.resourceLabel}`).join(', ')}`,
        );
      },
    };
  };

  // ---- reactive wiring -----------------------------------------------------
  effect(() => {
    const current = activeTab.value;
    for (const tab of tabs) {
      const on = tab.id === current;
      tab.panel.classList.toggle('hidden', !on);
      tabButtons.get(tab.id)?.classList.toggle('active', on);
    }
  });

  effect(() => {
    const mode = buyMode.value;
    for (const [id, btn] of buyModeButtons) {
      btn.classList.toggle('active', id === mode);
    }
  });

  effect(() => {
    const model = vm.value;
    // The engine clock is no longer shown, but stays observable for tests.
    panel.dataset.ticks = String(model.ticks);

    for (const tab of tabs) {
      if (tab.count(model) > 0) shownTabs.add(tab.id);
      tabButtons.get(tab.id)?.classList.toggle('hidden', !shownTabs.has(tab.id));
    }

    reconcile(topbar, topbarCache, model.resources, makeTopbarResource);
    fitTopbar();
    reconcile(generatorList, generatorCache, model.generators, makeGeneratorRow);
    reconcile(upgradeList, upgradeCache, model.upgrades, makeUpgradeCard);
    reconcile(
      breakthroughList,
      breakthroughCache,
      model.breakthroughs,
      makeBreakthroughRow,
    );
  });

  return {
    render(next: OverlayViewModel): void {
      vm.value = next;
    },
    toast(message: string): void {
      const node = el('div', { class: 'toast', text: message });
      tagComponent(node, 'toast');
      toastHost.append(node);
      window.setTimeout(() => {
        node.remove();
      }, 3200);
    },
    showModal(title: string, body: string): void {
      openInfoModal(title, body);
    },
    narrate(line: string): void {
      narrationText.textContent = line;
      narrationHost.classList.remove('hidden');
    },
  };
}
